import { findIndex, chain } from 'lodash';
import { assert, assertHasProperties } from 'common/assertions';
import { type BlockComponent } from 'types';
import { type Series, type Vif, type SoqlDataSource } from 'common/visualizations/vif';
import Constants from 'lib/Constants';
import Environment from 'StorytellerEnvironment';
import httpRequest from 'services/httpRequest.js';
import { type DeepPartial } from 'common/types/baseTypeExtensions';
import { migrateVif } from 'common/visualizations/helpers/migrateVif';
import type { AssetSelectorState } from 'store/reducers/AssetSelectorReducer';

interface DocumentData {
  data: {
    document: {
      url: string;
      original_url: string;
    };
  };
}

// The step in the asset selection flow the user is in.
export const WIZARD_STEP = {
  // Do you want a Socrata visualization, Youtube, an image, etc?
  SELECT_ASSET_PROVIDER: 'SELECT_ASSET_PROVIDER',

  ENTER_YOUTUBE_URL: 'ENTER_YOUTUBE_URL',

  ENTER_EMBED_CODE: 'ENTER_EMBED_CODE',

  ENTER_STORY_URL: 'ENTER_STORY_URL',

  // You want a Socrata visualization, so please choose an option
  SELECT_VISUALIZATION_OPTION: 'SELECT_VISUALIZATION_OPTION',
  // You want a measure, so please choose an option
  SELECT_MEASURE_OPTION: 'SELECT_MEASURE_OPTION',
  // You want something else, so please choose an option
  SELECT_OTHER_OPTION: 'SELECT_OTHER_OPTION',
  // You choose a dataset for Global Filter
  SELECT_DATASET_FOR_STORY_GLOBAL_FILTER: 'SELECT_DATASET_FOR_STORY_GLOBAL_FILTER',
  // You want a Socrata visualization, so please choose your dataset.
  SELECT_DATASET_FOR_VISUALIZATION: 'SELECT_DATASET_FOR_VISUALIZATION',
  // You choose a table.
  SELECT_TABLE_FROM_CATALOG: 'SELECT_TABLE_FROM_CATALOG',
  // You choose a map or chart visualization.
  SELECT_MAP_OR_CHART_OR_CALENDAR_VISUALIZATION_FROM_CATALOG:
    'SELECT_MAP_OR_CHART_OR_CALENDAR_VISUALIZATION_FROM_CATALOG',
  // You choose to embed a measure.
  SELECT_MEASURE_FROM_CATALOG: 'SELECT_MEASURE_FROM_CATALOG',
  // Do you want a card or chart?
  CONFIGURE_MEASURE: 'CONFIGURE_MEASURE',
  // You walk through the new authorship workflow.
  AUTHOR_VISUALIZATION: 'AUTHOR_VISUALIZATION',
  // You walk through the insitu measures authorship workflow.
  AUTHOR_MEASURE: 'AUTHOR_MEASURE',
  EDIT_MEASURE: 'EDIT_MEASURE',
  CONFIGURE_MAP_OR_CHART_OR_CALENDAR: 'CONFIGURE_MAP_OR_CHART_OR_CALENDAR',
  TABLE_PREVIEW: 'TABLE_PREVIEW',

  SELECT_IMAGE_TO_UPLOAD: 'SELECT_IMAGE_TO_UPLOAD',
  IMAGE_UPLOADING: 'IMAGE_UPLOADING',
  IMAGE_PREVIEW: 'IMAGE_PREVIEW',
  IMAGE_UPLOAD_ERROR: 'IMAGE_UPLOAD_ERROR',
};

export const getSignedUrls = async (documentId: string) => {
  const storyUid = Environment.STORY_UID;
  const documentUrl = `${Constants.API_PREFIX_PATH}/stories/${storyUid}/documents/${documentId}`;
  return await httpRequest('GET', documentUrl)
    .then(function ({ data }: DocumentData) {
      const documentResource = data.document;
      return {
        url: documentResource.url,
        originalUrl: documentResource.original_url,
      };
    })
    .catch((error: string) => _handleRequestError(error));
};

const _handleRequestError = (error: string) => {
  console.error(error);
  throw error;
};

// Returns series index of primary map layer from the VIF being edited
//
// Returns:
// seriesIndex: <number>
export const getPrimarySeriesIndex = (series: Series[]) => {
  const seriesIndex = findIndex(series, (item) => item.primary === true);
  return seriesIndex === -1 ? 0 : seriesIndex;
};

/** Returns the dataset information from the VIF being edited,
 * if any.
 *
 * @returns dataset id
 */
export const getDatasetInVif = (state: AssetSelectorState): string | null => {
  const value = state.componentProperties;
  // Format is null here, even tho it's not marked that way in the type.
  if (!value || !value.vif || !value.vif.format) {
    return null;
  }
  const vif = migrateVif(value.vif) as DeepPartial<Vif>;
  const series = vif.series || [];
  const seriesIndex = getPrimarySeriesIndex(series as Series[]);
  return (series[seriesIndex]?.dataSource as Partial<SoqlDataSource>)?.datasetUid || null;
};

const stepComponentDictionary = {
  hero: WIZARD_STEP.IMAGE_PREVIEW,
  image: WIZARD_STEP.IMAGE_PREVIEW,
  globalFilter: WIZARD_STEP.SELECT_DATASET_FOR_STORY_GLOBAL_FILTER,
  'story.tile': WIZARD_STEP.ENTER_STORY_URL,
  'story.widget': WIZARD_STEP.ENTER_STORY_URL,
  'youtube.video': WIZARD_STEP.ENTER_YOUTUBE_URL,
  embeddedHtml: WIZARD_STEP.ENTER_EMBED_CODE,
  author: WIZARD_STEP.IMAGE_PREVIEW, // Author blocks act like an image embed + RTE blurb.
  'socrata.visualization.vizCanvas': WIZARD_STEP.CONFIGURE_MAP_OR_CHART_OR_CALENDAR,
  'measure.card': WIZARD_STEP.CONFIGURE_MEASURE,
  'measure.chart': WIZARD_STEP.CONFIGURE_MEASURE,
};

/**
 * Given an asset type (i.e. 'socrata.visualization.vizCanvas'), returns
 * the step the wizard should start from given the user is updating
 * a component.
 */
export const getStepForComponent = (component: BlockComponent) => {
  return (
    stepComponentDictionary[component.type as keyof typeof stepComponentDictionary] ||
    (component.type.indexOf('socrata.visualization.') === 0
      ? WIZARD_STEP.AUTHOR_VISUALIZATION
      : // Something went wrong and we don't know where to pick up from (new embed type?),
        // so open the wizard at the very first step.
        WIZARD_STEP.SELECT_ASSET_PROVIDER)
  );
};

export const assertColumns = (state: AssetSelectorState) => {
  assertHasProperties(state, 'componentProperties.dataset.datasetUid', 'dataset.columns');
  // After assertHasProperties succeeds, we know these properties exist
  assert(
    state.dataset != null && state.dataset?.columns?.length > 0,
    'dataset must have at least one column',
  );
};

type StoryAndUid = {
  domain: string;
  storyUid: string;
};

export const componentPropertiesFromStoryUrl = (url: string): StoryAndUid | Record<string, never> => {
  const parsedUrl = _parseUrl(url);
  if (!parsedUrl) {
    return {};
  }

  if (parsedUrl.pathname.indexOf(Constants.VIEW_PREFIX_PATH) === 0) {
    // Find the last thing in the url that looks like a uid (4x4).
    // We can't take the first thing because our story title may look
    // like a 4x4, and it comes first.
    const uid = chain(parsedUrl.pathname.substring(Constants.VIEW_PREFIX_PATH.length).split('/'))
      .compact() // Removes blank strings.
      .findLast((c) => Boolean(c.match(Constants.FOUR_BY_FOUR_PATTERN)))
      .value();

    if (uid) {
      return { domain: parsedUrl.hostname, storyUid: uid };
    }
  }

  return {};
};

const _parseUrl = (url: string): HTMLAnchorElement | null => {
  if (url.match(/https?:\/\//)) {
    // Have to build the anchor node this way to work around IE11 behavior
    // where the leading slash was dropped from pathname.
    // Update from not 7 years ago, we don't support IE11 anymore but I ain't about to mess with this.
    return $(`<a href="${url}">`).get(0) as HTMLAnchorElement;
  } else {
    return null;
  }
};
